// BrandReviewStep.tsx — step 'brand-review' del onboarding.
// Qué hace: muestra y permite editar lo que el scan encontró de la marca
//   (descripción, productos, contacto, web y colores de marca con picker).
// Qué exporta: BrandReviewStep.
// Quién lo usa: CreateWorkspaceModal.

import { Button } from '../../Button';

// Validación de formato suave: solo avisa si el valor no parece válido, no bloquea.
// (los campos son opcionales — vacío nunca es "inválido")
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Acepta dígitos, espacios, +, -, (), . — pide al menos 7 dígitos reales.
const PHONE_RE = /^[+]?[\d\s().-]{7,}$/;
const isEmailInvalid = (v: string) => v.trim() !== '' && !EMAIL_RE.test(v.trim());
const isPhoneInvalid = (v: string) => {
  const t = v.trim();
  if (t === '') return false;
  return !PHONE_RE.test(t) || (t.match(/\d/g)?.length ?? 0) < 7;
};

const errBorder = '1px solid #ef4444';
const errMsgStyle: React.CSSProperties = { fontSize: 11, color: '#ef4444', marginTop: 4 };

interface Props {
  reviewDescription: string;
  setReviewDescription: (v: string) => void;
  reviewIndustry: string;
  setReviewIndustry: (v: string) => void;
  reviewPhone: string;
  setReviewPhone: (v: string) => void;
  reviewEmail: string;
  setReviewEmail: (v: string) => void;
  reviewWebsite: string;
  setReviewWebsite: (v: string) => void;
  reviewColors: string[];
  setReviewColors: React.Dispatch<React.SetStateAction<string[]>>;
  colorInput: string;
  setColorInput: (v: string) => void;
  confirmBrandReview: () => void;
}

export function BrandReviewStep({
  reviewDescription, setReviewDescription, reviewIndustry, setReviewIndustry,
  reviewPhone, setReviewPhone, reviewEmail, setReviewEmail, reviewWebsite, setReviewWebsite,
  reviewColors, setReviewColors, colorInput, setColorInput, confirmBrandReview,
}: Props) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ background: '#f8f9fb', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
        <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</label>
        <textarea
          value={reviewDescription}
          onChange={e => setReviewDescription(e.target.value)}
          rows={3}
          style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
          placeholder="What does this brand do?"
        />

        <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Products / Services</label>
        <input
          value={reviewIndustry}
          onChange={e => setReviewIndustry(e.target.value)}
          style={{ padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }}
          placeholder="e.g. Web design, Marketing, Coffee"
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</label>
            <input
              value={reviewPhone}
              onChange={e => setReviewPhone(e.target.value)}
              style={{ width: '100%', padding: 8, borderRadius: 6, border: isPhoneInvalid(reviewPhone) ? errBorder : '1px solid #e2e8f0', fontSize: 13, marginTop: 4 }}
              placeholder="+1 (555) 000-0000"
            />
            {isPhoneInvalid(reviewPhone) && <p style={errMsgStyle}>Enter a valid phone number</p>}
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email</label>
            <input
              value={reviewEmail}
              onChange={e => setReviewEmail(e.target.value)}
              style={{ width: '100%', padding: 8, borderRadius: 6, border: isEmailInvalid(reviewEmail) ? errBorder : '1px solid #e2e8f0', fontSize: 13, marginTop: 4 }}
              placeholder="contact@brand.com"
            />
            {isEmailInvalid(reviewEmail) && <p style={errMsgStyle}>Enter a valid email address</p>}
          </div>
        </div>

        <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Website</label>
        <input
          value={reviewWebsite}
          onChange={e => setReviewWebsite(e.target.value)}
          style={{ padding: 8, borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13 }}
          placeholder="https://yourbrand.com"
        />

        {/* Brand colors — interactive picker */}
        <div>
          <label style={{ fontWeight: 600, fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Brand Colors {reviewColors.length > 0 && <span style={{ color: '#3B82F6' }}>({reviewColors.length})</span>}
          </label>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {reviewColors.map((c, i) => (
              <div
                key={i}
                style={{ width: 32, height: 32, borderRadius: 8, background: c, border: '2px solid #e2e8f0', cursor: 'pointer', position: 'relative' }}
                title={`${c} — click to remove`}
                onClick={() => setReviewColors(prev => prev.filter((_, idx) => idx !== i))}
              >
                <span style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, opacity: 0.8 }}>×</span>
              </div>
            ))}
            {reviewColors.length < 6 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="color"
                  value={colorInput}
                  onChange={e => setColorInput(e.target.value)}
                  style={{ width: 32, height: 32, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 0 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (colorInput && !reviewColors.includes(colorInput)) {
                      setReviewColors(prev => [...prev, colorInput]);
                    }
                  }}
                  style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8f9fb', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                >
                  + Add
                </button>
              </div>
            )}
          </div>
          {reviewColors.length === 0 && (
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>No colors detected. Add your brand colors above — I&apos;ll use them in your ads!</p>
          )}
        </div>
      </div>

      <Button
        type="button"
        onClick={confirmBrandReview}
        disabled={isEmailInvalid(reviewEmail) || isPhoneInvalid(reviewPhone)}
        style={{ width: '100%' }}
      >
        Looks good, continue
      </Button>
    </div>
  );
}
